/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import BaseApi, {EventData} from '../api/BaseApi';
import {SettingsApi as Contract, Parameters, Settings, SettingsResponse} from '@adobe/exc-app/settings';
import {Events} from '../Events';
import Messenger, {messenger} from '../Messenger';
import Metrics from '../metrics';

enum SETTINGS_LEVEL {
  ORG = 'org',
  USER = 'user',
  USERORG = 'userorg'
}

type Handler = <T extends Settings>(reqParams: Parameters<T>) => Promise<SettingsResponse<T>>;

export default class SettingsApi extends BaseApi implements Contract {
  public keyPrefix = 'settings';
  public SETTINGS_LEVEL = SETTINGS_LEVEL;
  public get: Handler;
  public set: Handler;
  public metrics = Metrics.create('exc.module-runtime.settings');

  constructor(messenger: Messenger) {
    super(messenger);
    // Work around babel bug for now..
    this.get = this.settingsHandler('GET');
    this.set = this.settingsHandler('POST');
  }

  private settingsHandler(method: string) {
    return <T extends Settings>(reqParams: Parameters<T>): Promise<SettingsResponse<T>> => {
      const uniqueIndex = this.getUniqueID();
      this.metrics.info(`RuntimeSettings: Sending request ${uniqueIndex} to Sandbox`);
      this.messenger.send(Events.EXC_SETTINGS, {data: reqParams, id: uniqueIndex, method});
      return this.getPromise(uniqueIndex);
    };
  }

  public receive(event: MessageEvent): void {
    let {type, value}: EventData = event.data || {};

    if (type === Events.EXC_SETTINGS) {
      let promise = this.promises[value.id];
      promise && promise.resolve(value.reqResponse);
      this.metrics.info(`RuntimeSettings: Received response from sandbox for ${value.id}`);
      delete this.promises[value.id];
    }
  }
}

let instance: Contract;
export function settings() {
  if (!instance) {
    instance = new SettingsApi(messenger);
  }
  return instance;
}
